import { callOpenAI } from "../supabase/openAI/openAICall";

/**
 * Find the organizer that most closely matches the provided name
 * @param searchName - The name to search for
 * @param allOrganizers - List of all available organizers
 * @returns The matching organizer or null if no match is found
 */
export const findClosestOrganizerMatch = async (
  searchName: string,
  allOrganizers: { id: number; name: string }[]
) => {
  if (!searchName || !allOrganizers.length) return null;

  try {
    // Create the prompt with all organizer names and the query
    const organizerNames = allOrganizers.map((org) => org.name).join(", ");
    const prompt = `Here is a list of organizer names: ${organizerNames}.\n\nWhat name from this list is closest to: "${searchName}"?\n\nRespond with just the exact name from the list, nothing else.`;

    // Call OpenAI API via the utility function
    const trimmedName = await callOpenAI(prompt);

    if (!trimmedName) {
      return null;
    }

    // Find the organizer with this name
    const exactMatch = allOrganizers.find((org) => org.name === trimmedName);
    if (exactMatch) {
      return exactMatch;
    }

    // If no exact match, try case-insensitive match
    const caseInsensitiveMatch = allOrganizers.find(
      (org) => org.name.toLowerCase() === trimmedName?.toLowerCase()
    );
    if (caseInsensitiveMatch) {
      return caseInsensitiveMatch;
    }

    // As a last resort, look for partial matches
    return (
      allOrganizers.find(
        (org) =>
          org.name.toLowerCase().includes(trimmedName?.toLowerCase()) ||
          trimmedName?.toLowerCase().includes(org.name.toLowerCase())
      ) || null
    );
  } catch (error) {
    console.error("Error finding organizer match:", error);
    return null;
  }
};
